// ENTRAID-350: claiming an onboarding link with an existing onGuard login.
//
// The public claim answers 409 existing_login for an address that already has
// a login in another customer. The page then sends the person straight to
// sign-in and, back from the IdP, claims the link signed in
// (POST /onboarding/claim-existing). The token must survive that round trip.
//
// Where the token lives across the redirect: sessionStorage of this tab, under
// one key, for at most PENDING_TTL_MS, and removed as soon as the claim answers
// for good. Never in the IdP URL (the authorize request carries only the PKCE
// challenge, the state and the login hint) and never in the return path
// (/onboarding?claim=existing), so it is not written again into the browser
// history, the CloudFront access log of the return load or a Referer. Same tab
// only, like the sign-in transaction itself (lib/auth.js STORAGE_TX). It is the
// same value the person already holds in the invitation link; it grants nothing
// without a signed-in caller whose trusted email the server checks.
const STORAGE_PENDING_CLAIM = 'onguard.pendingClaim';
const STORAGE_CLAIM_ADDED = 'onguard.claimAdded';
export const PENDING_TTL_MS = 30 * 60_000;
// Onboarding tokens are "<base64url customerId>.<base64url secret>" (backend lib/onboarding-token.mjs); bounded and printable only.
const TOKEN_RE = /^[A-Za-z0-9._-]{16,512}$/;
const COMPANY_MAX = 120;

export const CLAIM_EXISTING_PATH = '/onboarding?claim=existing';

// The address the person typed (the login hint they sign in with): the return page claims only when the session's
// own email is this address (security review of PR 416). It is the same value as onguard.loginHint, same tab only.
const EMAIL_RE = /^[^\s@]{1,64}@[^\s@]{1,190}$/;

export function savePendingClaim({ token, companyName, email } = {}, now = Date.now()) {
  if (typeof token !== 'string' || !TOKEN_RE.test(token)) return false;
  const entry = { token, at: now };
  const address = typeof email === 'string' ? email.trim().toLowerCase() : '';
  if (EMAIL_RE.test(address)) entry.email = address;
  if (typeof companyName === 'string' && companyName.trim() && companyName.trim().length <= COMPANY_MAX) {
    entry.companyName = companyName.trim();
  }
  try {
    sessionStorage.setItem(STORAGE_PENDING_CLAIM, JSON.stringify(entry));
    return true;
  } catch {
    return false;
  }
}

/** The pending claim `{ token, companyName?, email? }`, or null when absent, malformed or older than PENDING_TTL_MS. */
export function readPendingClaim(now = Date.now()) {
  try {
    const entry = JSON.parse(sessionStorage.getItem(STORAGE_PENDING_CLAIM) || 'null');
    if (!entry || typeof entry !== 'object' || typeof entry.token !== 'string' || !TOKEN_RE.test(entry.token)) return null;
    if (!Number.isFinite(entry.at) || now - entry.at > PENDING_TTL_MS || entry.at > now + 60_000) {
      clearPendingClaim();
      return null;
    }
    const claim = { token: entry.token };
    if (typeof entry.companyName === 'string') claim.companyName = entry.companyName;
    if (typeof entry.email === 'string' && EMAIL_RE.test(entry.email)) claim.email = entry.email;
    return claim;
  } catch {
    return null;
  }
}

/**
 * ENTRAID-350 (TEST 2026-10-26): a pending claim runs BEFORE the entitlement page. A login whose current customer is
 * blocked or gone may still be adding a new one, so the portal goes to the claim page instead of showing the block.
 * The path to go to, or null when nothing is pending.
 */
export function pendingClaimRedirect(now = Date.now()) {
  return readPendingClaim(now) ? CLAIM_EXISTING_PATH : null;
}

export function clearPendingClaim() {
  try {
    sessionStorage.removeItem(STORAGE_PENDING_CLAIM);
  } catch {
    /* best-effort */
  }
}

/** Marks that this tab just added customerId to the login: the wizard shows a one-line notice once. */
export function rememberClaimAdded(customerId) {
  try {
    sessionStorage.setItem(STORAGE_CLAIM_ADDED, String(customerId));
  } catch {
    /* best-effort: only the notice is lost */
  }
}

/** One-shot: true once when this tab just added customerId to the login. */
export function takeClaimAdded(customerId) {
  try {
    const stored = sessionStorage.getItem(STORAGE_CLAIM_ADDED);
    if (!stored || stored !== String(customerId)) return false;
    sessionStorage.removeItem(STORAGE_CLAIM_ADDED);
    return true;
  } catch {
    return false;
  }
}
